import { ReactNode, useEffect, useState } from 'react';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/shared/components/carousel';

/**
 * 자동 재생, 루프, 페이지네이션 등을 지원하는 커스텀 캐러셀 컴포넌트입니다.
 * 내부적으로 shadcn 기반 Carousel을 사용합니다. (carousel.tsx)
 *
 * @param props.className - 추가적인 CSS 클래스
 * @param props.loop - 캐러셀 반복 여부
 * @param props.children - 캐러셀에 표시할 React 노드 배열
 * @param props.autoPlay - 자동 재생 옵션 (embla-carousel-autoplay의 옵션)
 * @param props.pagination - 현재 페이지와 전체 페이지 수를 전달받아 커스텀 페이지네이션 UI를 렌더링하는 함수
 * @param props.startIndex - 시작 인덱스 (기본값: 0)
 *
 * @returns 커스텀 캐러셀 UI 요소
 *
 * @example
 * <CustomCarousel
 *   loop
 *   autoPlay={{ delay: 3000 }}
 *   pagination={({ count, current }) => (
 *     <div>{current} / {count}</div>
 *   )}
 * >
 *   {[<Slide1 />, <Slide2 />, <Slide3 />]}
 * </CustomCarousel>
 */
export function CustomCarousel(props: {
  className?: string;
  loop?: boolean;
  children: ReactNode[];
  autoPlay?: AutoplayOptionsType;
  pagination?: (pageInfo: { count: number; current: number }) => ReactNode;
  startIndex?: number;
}) {
  const { className, loop, children, autoPlay, pagination, startIndex = 0 } = props;

  const plugins = [];
  if (autoPlay) {
    plugins.push(Autoplay(autoPlay));
  }

  const { setApi, count, current } = useCustomCarousel();

  return (
    <Carousel
      className={className}
      setApi={setApi}
      opts={{ loop: loop, startIndex: startIndex }}
      plugins={plugins}
    >
      <CarouselContent className="ml-0">
        {children.map((child, index) => (
          <CarouselItem className="pl-0" key={index}>
            {child}
          </CarouselItem>
        ))}
      </CarouselContent>
      {pagination?.({ count, current })}
    </Carousel>
  );
}

function useCustomCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return {
    count,
    current,
    setApi,
  };
}
